// PortalTooltip.tsx
import React, {
  ReactElement,
  ReactNode,
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type Placement = "top" | "bottom" | "left" | "right";

export type PortalTooltipProps = {
  content: ReactNode;
  placement?: Placement;
  className?: string;
  /** Toggle the small arrow on/off */
  arrow?: boolean; // ← you can enable/disable via this prop
  offset?: number;
  showDelay?: number;
  hideDelay?: number;
  disabled?: boolean;
  /** Must be a single element (React can clone it to attach handlers) */
  children: ReactElement;
};

const DEFAULT_ARROW_SIZE = 8;

const isBrowser =
  typeof window !== "undefined" && typeof document !== "undefined";

const PortalTooltip: React.FC<PortalTooltipProps> = ({
  content,
  placement = "bottom",
  className,
  arrow = true,
  offset = 8,
  showDelay = 80,
  hideDelay = 80,
  disabled = false,
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [arrowPos, setArrowPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const triggerRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  const tooltipId = useId();

  const clearTimers = () => {
    if (showTimer.current) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const computePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;

    const rect = trigger.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    let top = 0;
    let left = 0;

    switch (placement) {
      case "top":
        top = rect.top + scrollY - tipRect.height - offset;
        left = rect.left + scrollX + rect.width / 2 - tipRect.width / 2;
        break;
      case "bottom":
        top = rect.bottom + scrollY + offset;
        left = rect.left + scrollX + rect.width / 2 - tipRect.width / 2;
        break;
      case "left":
        top = rect.top + scrollY + rect.height / 2 - tipRect.height / 2;
        left = rect.left + scrollX - tipRect.width - offset;
        break;
      case "right":
        top = rect.top + scrollY + rect.height / 2 - tipRect.height / 2;
        left = rect.right + scrollX + offset;
        break;
    }

    setCoords({ top, left });

    // Arrow within tooltip box
    const ARROW_SIZE = DEFAULT_ARROW_SIZE;
    let aTop = 0;
    let aLeft = 0;

    switch (placement) {
      case "top":
        aTop = tipRect.height - 1;
        aLeft = tipRect.width / 2 - ARROW_SIZE;
        break;
      case "bottom":
        aTop = -ARROW_SIZE;
        aLeft = tipRect.width / 2 - ARROW_SIZE;
        break;
      case "left":
        aTop = tipRect.height / 2 - ARROW_SIZE;
        aLeft = tipRect.width - 1;
        break;
      case "right":
        aTop = tipRect.height / 2 - ARROW_SIZE;
        aLeft = -ARROW_SIZE;
        break;
    }

    setArrowPos({ top: aTop, left: aLeft });
  }, [placement, offset]);

  useLayoutEffect(() => {
    if (!visible || !isBrowser) return;

    computePosition();

    const onScroll = () => computePosition();
    const onResize = () => computePosition();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    const ro =
      "ResizeObserver" in window
        ? new (window as any).ResizeObserver(() => computePosition())
        : null;
    if (ro && triggerRef.current) ro.observe(triggerRef.current);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (ro && triggerRef.current) ro.unobserve(triggerRef.current);
    };
  }, [visible, computePosition]);

  useEffect(() => clearTimers, []);

  const show = () => {
    if (disabled) return;
    clearTimers();
    showTimer.current = window.setTimeout(() => setVisible(true), showDelay);
  };

  const hide = () => {
    clearTimers();
    hideTimer.current = window.setTimeout(() => setVisible(false), hideDelay);
  };

  // Safely merge original handlers with ours (typed)
  const childWithHandlers = useMemo(() => {
    const originalProps = (children.props ?? {}) as Partial<
      React.HTMLAttributes<HTMLElement>
    > & { tabIndex?: number };

    const mergedProps: Partial<React.HTMLAttributes<HTMLElement>> & {
      ref?: (node: HTMLElement | null) => void;
      tabIndex?: number;
      "aria-describedby"?: string;
    } = {
      ref: (node: HTMLElement | null) => {
        // assign our internal ref
        triggerRef.current = node;
        // forward to child's ref if present
        const originalRef: any = (children as any).ref;
        if (typeof originalRef === "function") originalRef(node);
        else if (originalRef && typeof originalRef === "object")
          originalRef.current = node;
      },
      onMouseEnter: (e) => {
        originalProps.onMouseEnter?.(e as any);
        show();
      },
      onMouseLeave: (e) => {
        originalProps.onMouseLeave?.(e as any);
        hide();
      },
      onFocus: (e) => {
        originalProps.onFocus?.(e as any);
        show();
      },
      onBlur: (e) => {
        originalProps.onBlur?.(e as any);
        hide();
      },
      "aria-describedby": visible ? tooltipId : undefined,
    };

    // Ensure focusable if non-interactive HTML tag
    const isDomTag = typeof children.type === "string";
    if (
      isDomTag &&
      ["span", "div", "p"].includes(children.type as string) &&
      originalProps.tabIndex == null
    ) {
      mergedProps.tabIndex = 0;
    }

    return cloneElement(children, mergedProps);
  }, [children, visible]);

  const bubble = visible ? (
    <div
      id={tooltipId}
      ref={tipRef}
      role="tooltip"
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        zIndex: 9999,
        pointerEvents: "none",
      }}
      className={[
        // Dark Flowbite-like bubble:
        "rounded-lg border shadow-lg ",
        "border-gray-700 bg-gray-900 text-white",
        "px-3 py-2 font-medium text-sm leading-snug",
        "max-w-xs break-words",
        "transition-opacity duration-100 ease-out",
        className ?? "",
      ].join(" ")}
      onMouseEnter={() => clearTimers()}
      onMouseLeave={() => hide()}
    >
      {content}
      {arrow && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: arrowPos.top,
            left: arrowPos.left,
            width: 0,
            height: 0,
            borderStyle: "solid",
            ...(placement === "top" && {
              borderWidth: `${DEFAULT_ARROW_SIZE}px ${DEFAULT_ARROW_SIZE}px 0 ${DEFAULT_ARROW_SIZE}px`,
              borderColor: "#111827 transparent transparent transparent",
            }),
            ...(placement === "bottom" && {
              borderWidth: `0 ${DEFAULT_ARROW_SIZE}px ${DEFAULT_ARROW_SIZE}px ${DEFAULT_ARROW_SIZE}px`,
              borderColor: "transparent transparent #111827 transparent",
            }),
            ...(placement === "left" && {
              borderWidth: `${DEFAULT_ARROW_SIZE}px 0 ${DEFAULT_ARROW_SIZE}px ${DEFAULT_ARROW_SIZE}px`,
              borderColor: "transparent transparent transparent #111827",
            }),
            ...(placement === "right" && {
              borderWidth: `${DEFAULT_ARROW_SIZE}px ${DEFAULT_ARROW_SIZE}px ${DEFAULT_ARROW_SIZE}px 0`,
              borderColor: "transparent #111827 transparent transparent",
            }),
          }}
        />
      )}
    </div>
  ) : null;

  if (!isBrowser) return children; // SSR safety

  return (
    <>
      {childWithHandlers}
      {visible && bubble ? createPortal(bubble, document.body) : null}
    </>
  );
};

export default PortalTooltip;
